import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { BetaBanner } from "@/components/BetaBanner";
import { initAnalyticsIfConsented, trackPageview } from "@/lib/analytics";
import Home from "@/pages/Home";
import Results from "@/pages/Results";
import SchoolDetail from "@/pages/SchoolDetail";
import PersonalArea from "@/pages/PersonalArea";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import TermsOfService from "@/pages/TermsOfService";
import Disclaimer from "@/pages/Disclaimer";
import AuthCallback from "@/pages/AuthCallback";
import TopHub from "@/pages/TopHub";
import TopByCity from "@/pages/TopByCity";
import SuggestSchool from "@/pages/SuggestSchool";
import CorrectSchool from "@/pages/CorrectSchool";
import AdminReports from "@/pages/AdminReports";
import ProductsHub from "@/pages/ProductsHub";
import ProductCategory from "@/pages/ProductCategory";
import ProductDetail from "@/pages/ProductDetail";
import SuggestProduct from "@/pages/SuggestProduct";
import UserProfile from "@/pages/UserProfile";
import BlogPapaDito from "@/pages/BlogPapaDito";
import ArticleDetail from "@/pages/ArticleDetail";
import ReviewLanding from "@/pages/ReviewLanding";
import RaccontaLanding from "@/pages/RaccontaLanding";
import GuidaSceltaScuola from "@/pages/GuidaSceltaScuola";
import AnteprimaSchedaDemo from "@/pages/AnteprimaSchedaDemo";
import ScrollToTop from "@/components/ScrollToTop";

function AppRouter() {
  const location = useLocation();

  // Bootstrap GA4 once — only activates if user has already consented in a previous visit.
  useEffect(() => {
    initAnalyticsIfConsented();
    // React on consent changes (e.g., after clicking "Accept all" in banner)
    const onConsent = () => trackPageview(location.pathname + location.search);
    window.addEventListener("permanina:consent-changed", onConsent);
    return () => window.removeEventListener("permanina:consent-changed", onConsent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track pageview on every route change (SPA navigation)
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <NavBar />
      <BetaBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/cerca" element={<Results />} />
        <Route path="/scuola/:id" element={<SchoolDetail />} />
        <Route path="/migliori" element={<TopHub />} />
        <Route path="/migliori/:slug" element={<TopByCity />} />
        <Route path="/area-personale" element={<PersonalArea />} />
        <Route path="/suggerisci" element={<SuggestSchool />} />
        <Route path="/scuola/:id/correggi" element={<CorrectSchool />} />
        <Route path="/prodotti" element={<ProductsHub />} />
        <Route path="/prodotti/:slug" element={<ProductCategory />} />
        <Route path="/prodotto/:slug" element={<ProductDetail />} />
        <Route path="/suggerisci-prodotto" element={<SuggestProduct />} />
        <Route path="/consiglia-materiale" element={<SuggestProduct />} />
        <Route path="/utente/:slug" element={<UserProfile />} />
        <Route path="/blog" element={<BlogPapaDito />} />
        <Route path="/blog/:slug" element={<ArticleDetail />} />
        <Route path="/recensisci" element={<ReviewLanding />} />
        <Route path="/racconta" element={<RaccontaLanding />} />
        <Route path="/guida-scelta-scuola" element={<GuidaSceltaScuola />} />
        <Route path="/guida-pronti-primaria" element={<GuidaSceltaScuola />} />
        <Route path="/anteprima-scheda" element={<AnteprimaSchedaDemo />} />
        <Route path="/admin/segnalazioni" element={<AdminReports />} />
        <Route path="/info/privacy" element={<PrivacyPolicy />} />
        <Route path="/info/cookie" element={<CookiePolicy />} />
        <Route path="/info/termini" element={<TermsOfService />} />
        <Route path="/info/disclaimer" element={<Disclaimer />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen bg-[#FAF9F6]">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
